import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Sparkles, MessageCircle, Shield, Users, TrendingUp, Star, Quote } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Landing() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [stats, setStats] = useState({
    men_count: 0,
    women_count: 0,
    matches_count: 0,
    total_users: 0
  });
  const [testimonials, setTestimonials] = useState([]);
  const [reviewMeta, setReviewMeta] = useState({ average_rating: 0, count: 0 });

  useEffect(() => {
    // Fetch public stats
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/stats/public`);
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };
    fetchStats();

    // Fetch top approved testimonials for the social-proof carousel
    axios.get(`${API}/testimonials`)
      .then(({ data }) => {
        const top = [...(data.testimonials || [])]
          .sort((a, b) => b.rating - a.rating)
          .slice(0, 3);
        setTestimonials(top);
        setReviewMeta({ average_rating: data.average_rating || 0, count: data.count || 0 });
      })
      .catch(() => {});
  }, []);

  const TT = {
    fr: { heading: "Ils ont trouvé l'amour", reviews: 'avis vérifiés', years: 'ans', viewAll: 'Voir tous les témoignages' },
    en: { heading: 'They found love', reviews: 'verified reviews', years: 'y.o.', viewAll: 'See all testimonials' },
    es: { heading: 'Encontraron el amor', reviews: 'opiniones verificadas', years: 'años', viewAll: 'Ver todos los testimonios' },
    de: { heading: 'Sie fanden die Liebe', reviews: 'verifizierte Bewertungen', years: 'J.', viewAll: 'Alle Erfahrungsberichte ansehen' },
  };
  const tt = TT[language] || TT.fr;

  const renderStars = (count) => Array.from({ length: 5 }).map((_, i) => (
    <Star key={i} className={`w-4 h-4 ${i < count ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} aria-hidden="true" />
  ));

  return (
    <main className="min-h-screen" role="main">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" aria-label="Hero section">
        {/* Language Switcher */}
        <nav className="absolute top-6 right-6 z-20" role="navigation" aria-label="Language selection">
          <LanguageSwitcher />
        </nav>

        <div className="absolute inset-0 z-0" aria-hidden="true">
          <div className="absolute top-20 left-10 w-72 h-72 bg-pink-300 rounded-full opacity-20 blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-orange-300 rounded-full opacity-20 blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <article className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <header className="flex items-center justify-center mb-6">
            <Heart className="w-16 h-16 text-pink-500 fill-pink-500 animate-pulse" aria-hidden="true" />
          </header>
          
          <h1 className="text-6xl sm:text-7xl lg:text-8xl font-bold mb-6 gradient-text">
            {t('landing.title')}
          </h1>
          
          <p className="text-xl sm:text-2xl lg:text-3xl text-gray-700 mb-4 font-light">
            {t('landing.subtitle')}
          </p>
          
          <p className="text-base sm:text-lg text-gray-600 mb-12 max-w-2xl mx-auto">
            {t('landing.description')}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center" role="group" aria-label="Call to action buttons">
            <button
              data-testid="landing-signup-btn"
              onClick={() => navigate('/auth?mode=signup')}
              className="btn-primary text-lg px-12 py-4"
            >
              {t('landing.btnSignup')}
            </button>
            <button
              data-testid="landing-login-btn"
              onClick={() => navigate('/auth?mode=login')}
              className="btn-secondary text-lg px-12 py-4"
            >
              {t('landing.btnLogin')}
            </button>
          </div>
        </article>
      </section>

      {/* Stats Section - Hidden as requested */}
      {/* 
      <section className="py-16 px-6 bg-gradient-to-r from-pink-50 to-orange-50" aria-label="Statistics">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="glass-card p-6 text-center transform hover:scale-105 transition-transform">
              <div className="flex items-center justify-center mb-3">
                <Users className="w-8 h-8 text-blue-500" />
              </div>
              <div className="text-3xl font-bold text-gray-800 mb-1">
                {stats.men_count}
              </div>
              <div className="text-sm text-gray-600">{t('stats.menRegistered')}</div>
            </div>

            <div className="glass-card p-6 text-center transform hover:scale-105 transition-transform">
              <div className="flex items-center justify-center mb-3">
                <Users className="w-8 h-8 text-pink-500" />
              </div>
              <div className="text-3xl font-bold text-gray-800 mb-1">
                {stats.women_count}
              </div>
              <div className="text-sm text-gray-600">{t('stats.womenRegistered')}</div>
            </div>

            <div className="glass-card p-6 text-center transform hover:scale-105 transition-transform">
              <div className="flex items-center justify-center mb-3">
                <Heart className="w-8 h-8 text-pink-500 fill-pink-500" />
              </div>
              <div className="text-3xl font-bold text-gray-800 mb-1">
                {stats.matches_count}
              </div>
              <div className="text-sm text-gray-600">{t('stats.successfulMatches')}</div>
            </div>

            <div className="glass-card p-6 text-center transform hover:scale-105 transition-transform">
              <div className="flex items-center justify-center mb-3">
                <TrendingUp className="w-8 h-8 text-green-500" />
              </div>
              <div className="text-3xl font-bold text-gray-800 mb-1">
                {stats.total_users}
              </div>
              <div className="text-sm text-gray-600">{t('stats.activeMembers')}</div>
            </div>
          </div>
        </div>
      </section>
      */}

      {/* Features Section */}
      <section className="py-24 px-6" aria-labelledby="features-heading">
        <div className="max-w-6xl mx-auto">
          <h2 id="features-heading" className="text-4xl sm:text-5xl font-bold text-center mb-16 gradient-text">
            {t('landing.whyTitle')}
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8" role="list">
            <article className="glass-card p-8 text-center hover:scale-105" style={{ transition: 'transform 0.3s' }} role="listitem">
              <Sparkles className="w-12 h-12 mx-auto mb-4 text-pink-500" aria-hidden="true" />
              <h3 className="text-xl font-bold mb-3 text-gray-800">{t('landing.aiAdvanced')}</h3>
              <p className="text-gray-600">{t('landing.aiAdvancedDesc')}</p>
            </article>

            <article className="glass-card p-8 text-center hover:scale-105" style={{ transition: 'transform 0.3s' }} role="listitem">
              <Heart className="w-12 h-12 mx-auto mb-4 text-pink-500 fill-pink-500" aria-hidden="true" />
              <h3 className="text-xl font-bold mb-3 text-gray-800">{t('landing.smartMatching')}</h3>
              <p className="text-gray-600">{t('landing.smartMatchingDesc')}</p>
            </article>

            <article className="glass-card p-8 text-center hover:scale-105" style={{ transition: 'transform 0.3s' }} role="listitem">
              <MessageCircle className="w-12 h-12 mx-auto mb-4 text-pink-500" aria-hidden="true" />
              <h3 className="text-xl font-bold mb-3 text-gray-800">{t('landing.chatAssisted')}</h3>
              <p className="text-gray-600">{t('landing.chatAssistedDesc')}</p>
            </article>

            <article className="glass-card p-8 text-center hover:scale-105" style={{ transition: 'transform 0.3s' }} role="listitem">
              <Shield className="w-12 h-12 mx-auto mb-4 text-pink-500" aria-hidden="true" />
              <h3 className="text-xl font-bold mb-3 text-gray-800">{t('landing.secure')}</h3>
              <p className="text-gray-600">{t('landing.secureDesc')}</p>
            </article>
          </div>
        </div>
      </section>

      {/* Testimonials Section (social proof) */}
      {testimonials.length > 0 && (
        <section className="py-24 px-6 bg-gradient-to-b from-transparent to-pink-50/50" aria-labelledby="home-testimonials-heading" data-testid="home-testimonials">
          <div className="max-w-6xl mx-auto">
            <h2 id="home-testimonials-heading" className="text-4xl sm:text-5xl font-bold text-center mb-4 gradient-text">
              {tt.heading}
            </h2>
            <div className="flex items-center justify-center gap-2 mb-12" aria-label={`${reviewMeta.average_rating}/5`}>
              <div className="flex">{renderStars(Math.round(reviewMeta.average_rating))}</div>
              <span className="text-gray-800 font-semibold">{reviewMeta.average_rating}/5</span>
              <span className="text-gray-500 text-sm">· {reviewMeta.count} {tt.reviews}</span>
            </div>

            <div className="grid md:grid-cols-3 gap-8" role="list">
              {testimonials.map((item) => (
                <article key={item.id} className="glass-card p-6 flex flex-col hover:scale-105" style={{ transition: 'transform 0.3s' }} role="listitem" data-testid="home-testimonial-card">
                  <Quote className="w-8 h-8 text-pink-200 mb-3" aria-hidden="true" />
                  <p className="text-gray-700 leading-relaxed mb-4 flex-1">
                    "{item.story.length > 180 ? item.story.slice(0, 180) + '…' : item.story}"
                  </p>
                  <div className="flex mb-4">{renderStars(item.rating)}</div>
                  <div className="flex items-center gap-3">
                    <img
                      src={`https://ui-avatars.com/api/?name=${encodeURIComponent(item.name)}&background=ec4899&color=fff&size=96&bold=true`}
                      alt={item.name}
                      className="w-11 h-11 rounded-full flex-shrink-0"
                      loading="lazy"
                    />
                    <div>
                      <div className="font-bold text-gray-800 text-sm">{item.name}</div>
                      <div className="text-xs text-gray-500">{item.age} {tt.years} · {item.location}</div>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <div className="text-center mt-12">
              <button
                onClick={() => navigate(`/${language}/testimonials`)}
                className="btn-secondary px-8 py-3"
                data-testid="home-testimonials-viewall"
              >
                {tt.viewAll}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-24 px-6" aria-labelledby="cta-heading">
        <article className="max-w-4xl mx-auto glass-card p-12 text-center">
          <h2 id="cta-heading" className="text-4xl font-bold mb-6 gradient-text">
            {t('landing.readyTitle')}
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            {t('landing.readyDesc')}
          </p>
          <button
            data-testid="landing-cta-btn"
            onClick={() => navigate('/auth?mode=signup')}
            className="btn-primary text-lg px-16 py-4"
            aria-label="Créer un compte gratuit"
          >
            {t('landing.btnCta')}
          </button>
        </article>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-white/50" role="contentinfo">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="font-bold text-gray-800 mb-4">A-More AI</h3>
              <p className="text-sm text-gray-600">
                {t('menu.tagline')}
              </p>
            </div>
            
            <div>
              <h3 className="font-bold text-gray-800 mb-4">{t('menu.discover')}</h3>
              <nav className="flex flex-col gap-2">
                <button onClick={() => navigate(`/${language}/how-it-works`)} className="text-sm text-gray-600 hover:text-pink-600 text-left">{t('menu.howItWorks')}</button>
                <button onClick={() => navigate(`/${language}/about`)} className="text-sm text-gray-600 hover:text-pink-600 text-left">{t('menu.about')}</button>
                <button onClick={() => navigate(`/${language}/testimonials`)} className="text-sm text-gray-600 hover:text-pink-600 text-left">{t('menu.testimonials')}</button>
                <button onClick={() => navigate(`/${language}/faq`)} className="text-sm text-gray-600 hover:text-pink-600 text-left">{t('menu.faq')}</button>
                <button onClick={() => navigate(`/${language}/blog`)} className="text-sm text-gray-600 hover:text-pink-600 text-left">{t('menu.blog')}</button>
              </nav>
            </div>
            
            <div>
              <h3 className="font-bold text-gray-800 mb-4">{t('menu.legal')}</h3>
              <nav className="flex flex-col gap-2">
                <button onClick={() => navigate(`/${language}/terms`)} className="text-sm text-gray-600 hover:text-pink-600 text-left">{t('menu.terms')}</button>
                <button onClick={() => navigate(`/${language}/terms`)} className="text-sm text-gray-600 hover:text-pink-600 text-left">{t('menu.privacy')}</button>
              </nav>
            </div>
            
            <div>
              <h3 className="font-bold text-gray-800 mb-4">Contact</h3>
              <a href="mailto:support@a-more-ai.com" className="text-sm text-gray-600 hover:text-pink-600">
                support@a-more-ai.com
              </a>
            </div>
          </div>
          
          <div className="text-center pt-8 border-t border-gray-200">
            <p className="text-gray-600 text-sm">
              © 2025 A-More AI. {language === 'en' ? 'All rights reserved.' : language === 'es' ? 'Todos los derechos reservados.' : language === 'de' ? 'Alle Rechte vorbehalten.' : 'Tous droits réservés.'}
            </p>
            <p className="text-gray-400 text-xs mt-2">
              <button 
                onClick={() => navigate(`/${language}/admin`)}
                className="hover:text-pink-600 transition-colors"
              >
                Admin
              </button>
            </p>
          </div>
        </div>
      </footer>
    </main>
  );
}
